<template>
    <div style="height: 100%;">
        <div id="main" v-show="!rotaAninhada">
            <container-loader
                v-if="isLoading && !$store.getters.isLoading"
            />
            <div class="page">
                <div class="tipos-servicos vertical-flow">
                    <div class="listing-header">
                        <div class="title-breadcrumb">
                            <h1 class="title">{{ $t("motivo-despesa") }}</h1>
                            <nav class="breadcrumb" aria-label="breadcrumbs">
                                <ul>
                                    <li>
                                        <a class="a-inativo">{{
                                            $t("despesas")
                                        }}</a>
                                    </li>
                                    <li>
                                        <a aria-current="page">{{
                                            $t("motivo-despesa")
                                        }}</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                        <div class="header-buttons">
                            <button
                                v-if="permissao.create && !integracao"
                                class="save-btn"
                                type="button"
                                tag="button"
                                @click="
                                    $router.push({
                                        name: 'des-motivo-despesa-new',
                                    })
                                "
                            >
                                {{ $t("adicionar") }}
                            </button>
                        </div>
                    </div>

                    <div class="search-and-filter">
                        <div class="search-bar">
                            <search-bar
                                :onSearch="debouncedOnSearch"
                            />
                        </div>

                        <div class="situacao-select">
                            <label v-text="$t('situacao')" />
                            <v-select
                                ref="situacaoInput"
                                class="is-100"
                                label="label"
                                :options="situacoes"
                                @input="onSituacaoChanged"
                                v-model="situacao"
                            >
                                <template slot="option" slot-scope="option">
                                    {{ $t(option.label) }}
                                </template>
                                <template
                                    slot="selected-option"
                                    slot-scope="option"
                                >
                                    {{ $t(option.label) }}
                                </template>
                                <template slot="no-options">{{
                                    $t("sem-resultados")
                                }}</template>
                            </v-select>
                        </div>
                    </div>

                    <div class="registry-wrapper">
                        <empty-table-message v-if="isEmpty" />
                        <b-table
                            v-else
                            :data="motivosDespesas"
                            class="data-table"
                            @sort="sortTable"
                            backend-sorting
                        >
                            <b-table-column
                                v-for="(f, i) in fields"
                                :key="i"
                                :field="f.key"
                                :label="f.key != 'excluir' ? $t(f.key) : ''"
                                :width="f.width"
                                :class="{
                                    'excluir-field': f.key == 'situacao',
                                    'label-km': f.key == 'label',
                                }"
                                :sortable="f.sortable"
                                :custom-sort="f.sortFn"
                                v-slot="props"
                            >
                                <div v-if="f.key == 'icone'">
                                    <!--gerando html com v-html par icones dinamicos -->
                                    <span
                                        v-html="buildIcon(props.row[f.key])"
                                        ref="motivoDespesaIcon"
                                        class="icon has-text-dark"
                                    />
                                </div>
                                <div v-else-if="f.key == 'cor'">
                                    <div
                                        class="color-block"
                                        :style="{
                                            'background-color': props.row[f.key],
                                        }"
                                    ></div>
                                </div>

                                <div v-else-if="f.key == 'label'">
                                    <div v-if="props.row[f.key] == 1">
                                        <div 
                                            class="bar a-km" 
                                            v-tippy 
                                            :title="$t(
                                                'despesa-automatica-km-maps'
                                            )"
                                        />
                                    </div>
                                    <div v-else-if="props.row[f.key] == 2">
                                        <div 
                                            class="bar b-km" 
                                            v-tippy 
                                            :title="$t(
                                                'despesa-automatica-km-manual'
                                            )"
                                        />
                                    </div>
                                </div>

                                <div v-else-if="f.key == 'descricao'">
                                    <router-link
                                        :to="{
                                            name: 'des-motivo-despesa-edit',
                                            params: { id: props.row.id },
                                        }"
                                    >
                                        <pre>{{ props.row[f.key] }}</pre>
                                    </router-link>
                                </div>

                                <div v-else-if="f.key == 'situacao'">
                                    <b-tag
                                        class="table-tag table-tag-full-width"
                                        v-if="props.row[f.key] == 1"
                                        type="is-success"
                                        size="is-medium"
                                        >{{ $t("ativo") }}</b-tag
                                    >
                                    <b-tag
                                        class="table-tag table-tag-full-width"
                                        v-else
                                        type="is-danger"
                                        size="is-medium"
                                        >{{ $t("inativo") }}</b-tag
                                    >
                                </div>
                                <div v-else-if="f.key == 'excluir'">
                                    <span class="float-right">
                                        <span v-if="props.row.situacao == 1">
                                            <b-button
                                                v-if="
                                                    permissao.delete &&
                                                    !integracao
                                                "
                                                class="action-button"
                                                type="is-danger"
                                                icon-right="delete"
                                                @click="
                                                    promptForDesativar(props.row)
                                                "
                                                v-tippy 
                                                :title="$t('desativar')"
                                            />
                                        </span>
                                        <b-button
                                            v-else-if="
                                                permissao.update &&
                                                !integracao
                                            "
                                            class="action-button"
                                            type="is-success"
                                            icon-right="check"
                                            @click="
                                                promptForReativar(props.row)
                                            "
                                            v-tippy 
                                            :title="$t('reativar')"
                                        />
                                    </span>
                                </div>

                                <div v-else>
                                    {{ props.row[f.key] }}
                                </div>
                            </b-table-column>
                        </b-table>
                        <PaginationViewer
                            class="mb-0"
                            :resetPagination="pagination.resetPagination"
                            :totalItems="pagination.totalRows"
                            :perPageOptions="pagination.perPageOptions"
                            :optionStarted="pagination.optionStarted"
                            @change="updatePagination"
                        />
                    </div>
                </div>
            </div>
        </div>
        <router-view />
    </div>
</template>

<script>
import ContainerLoader from "@/components/content/loader/ContainerLoader";
import MotivoDespesasService from "@/services/despesas/MotivoDespesasService";

export default {
    beforeRouteUpdate(to, from, next) {
        if (
            to.name.includes("des-motivo-despesa") &&
            to.name != "des-motivo-despesa-list"
        ) {
            this.rotaAninhada = true;
            next();
        } else {
            this.rotaAninhada = false;
            this.carregarDados(this.pagination.currentPage);
            next();
        }
    },
    components: {
        ContainerLoader,
    },
    data() {
        return {
            rotaAninhada: false,
            integracao:
                this.$store.state.user.tabelasERPWebServer.includes("DESPESA_MOTIVO"),
            motivosDespesas: [],
            situacoes: [
                { id: 2, label: "todos" },
                { id: 1, label: "ativos" },
                { id: 0, label: "inativos" },
            ],
            situacao: { id: 2, label: "todos" },
            service: new MotivoDespesasService(this.$http, this.$router),
            fields: [
                { key: "label", width: 5, sortable: false, sortFn: null },
                { key: "codigo", width: 55, sortable: true, sortFn: null },
                { key: "icone", width: 70, sortable: false, sortFn: null },
                { key: "cor", width: 70, sortable: false, sortFn: null },
                { key: "descricao", width: null, sortable: true, sortFn: null },
                { key: "situacao", width: 70, sortable: false, sortFn: null },
                { key: "excluir", width: 20, sortable: false, sortFn: null },
            ],
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            isLoading: false,
            isEmpty: false,
            debouncedOnSearch: null,
            delete: null,
            queryFilter: "",
            column: "descricao",
            order: "asc",
            permissao: {},
        };
    },
    created() {
        this.rotaAninhada = (
            this.$route.name.includes("des-motivo-despesa") &&
            this.$route.name != "des-motivo-despesa-list"
        );

        this.pagination.perPage =
            this.pagination.perPageOptions[this.pagination.optionStarted];

        this.permissao = this.hasPermissionAccess(
            "despesas",
            "cadastros",
            "des-motivo-despesa"
        );

        if (!this.permissao.read) {
            this.$router.push({
                name: "acesso-nao-permitido",
                params: { tela: "motivo-despesa" },
            });
        } else {
            this.debouncedOnSearch = this.debounce(this.onSearch, 500);
            this.carregarDados();
        }
    },
    methods: {
        onSearch(value) {
            this.queryFilter = this.removeAcentos(value);
            this.pagination.currentPage = 1;
            this.carregarDados();
        },
        onSituacaoChanged(val) {
            let temp = null;
            if (val) {
                temp = this.situacoes.filter((v) => v.id == val.id);
                this.situacao = temp[0];
            } else {
                this.situacao = this.situacoes[0];
            }
            this.carregarDados();
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        carregarDados(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;
            this.isLoading = true;
            this.service
                .findAll(
                    this.column,
                    this.order,
                    currentPage - 1,
                    this.pagination.perPage,
                    this.situacao.id,
                    this.queryFilter
                )
                .then(this.mapItems)
                .then((value) => {
                    this.motivosDespesas = value;
                    this.isEmpty = this.motivosDespesas.length <= 0;
                })
                .catch((e) => {
                    this.isEmpty = true;
                    this.$root.showMessage("error", "msg-erro-load");
                    console.error("Erro ao carregar os acertos.");
                })
                .finally(() => {
                    this.isLoading = false;
                    this.pagination.resetPagination = false;
                });
        },
        mapItems(body) {
            this.pagination.totalRows = body.totalElements;
            let list = body.content.map((m) => {
                return {
                    id: m.iddespesa_motivo,
                    codigo: m.iddespesa_motivoerp,
                    descricao: m.descricao,
                    cor: m.cor,
                    icone: m.icone.toLowerCase(),
                    situacao: m.situacao,
                    label: m.despesaautomatica,
                    excluir: "excluir",
                };
            });
            return list;
        },
        sortTable(field, order) {
            this.column = field;
            this.order = order;
            this.carregarDados();
        },
        promptForDesativar(row) {
            this.delete = row;
            let message = this.$t("msg-confirmacao-desativacao-n", { n: row.descricao });
            if (row.label == 1)
                message =
                    this.$t(
                        "msg-confirmacao-desativacao-despesa-auto-km"
                    ) +
                    "<br>" +
                    message;
            this.$root.confirm({
                message: message,
                onConfirm: this.updateSituacaoRegistro,
                onCancel: this.removePrompt,
            });
        },
        promptForReativar(row) {
            this.delete = row;
            this.$root.confirm({
                message: this.$t("msg-confirmacao-reativacao-n", { n: row.descricao }),
                onConfirm: this.updateSituacaoRegistro,
                onCancel: this.removePrompt,
            });
        },
        buildIcon(icon) {
            return `<i class="fa-lg ${icon}"></i>`;
        },
        removePrompt() {
            this.delete = null;
        },
        updateSituacaoRegistro() {
            this.isLoading = true;

            let motivodespesa = {
                iddespesa_motivo: this.delete.id,
                situacao: this.delete.situacao == 1 ? 0 : 1,
            };

            this.service
                .updateSituacao(motivodespesa)
                .then((r) => {
                    if (r.status == 200) {
                        if (r.content.situacao == 1) {
                            this.$root.showMessage(
                                "success",
                                "msg-sucesso-ao-reativar-mot-despesa"
                            );
                        } else if (r.content.situacao == 0) {
                            this.$root.showMessage(
                                "success",
                                "msg-sucesso-ao-desativar-mot-despesa"
                            );
                        }
                        this.carregarDados(this.pagination.currentPage);
                    } else if (r.status == 500) {
                        this.$root.showMessage(
                            "error",
                            "msg-erro-ao-desativar-mot-despesa"
                        );
                    }
                })
                .catch((e) => {
                    console.error(
                        "Erro ao atualizar status do Motivo despesa.",
                        e
                    );
                    this.$root.showMessage(
                        "error",
                        "msg-erro-ao-desativar-mot-despesa"
                    );
                })
                .finally((_) => {
                    this.isLoading = false;
                    this.delete = null;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: 100%;

    .page {
        position: relative;
        padding-left: 0;

        .table-tag {
            width: 5rem !important;
        }

        .tipos-servicos .search-and-filter {
            display: flex;
            justify-content: space-between;
            margin: 10px 0;

            .search-bar {
                width: 80%;
                padding-right: 10px;
            }
            .situacao-select {
                width: 30%;
                div {
                    height: 40px;
                }
            }
            .filter-bar {
                width: 31%;
            }
            @media (max-width: 760px) {
                .search-bar {
                    padding-right: 0;
                }
                .situacao-select {
                    width: 100%;
                }
            }
        }
        .label-km {
            padding-left: 0;
            padding-right: 0;
        }
        .bar {
            border-radius: 100px;
            width: 5px;
            height: 26px;
            position: absolute;
            top: -17px;
            &.a-km {
                background-color: #5f971e;
            }
            &.b-km {
                background-color: #891e97;
            }
        }
        .bar-column {
            padding-left: 0.01rem;
            padding-right: 0.01rem;
        }
        @media (max-width: 760px) {
            .label-km {
                padding-left: 5px;
                padding-right: 20px;
                justify-content: left;
            }
        }
    }
}
</style>
